/**
 * ambient_life — the PURE rules of the `ambientLife` graphics setting (gaps wave round 2, 2026-09-28).
 *
 * The preset flag existed since the 2026-09-25 realism wave ("Ambient life particles: falling leaves in woods, pollen
 * in meadows, dust devils in deserts (under particleCap). Live.") and nothing read it — R12 was deferred with its menu
 * row removed (impl-vegetation.md "What is left" §1 is the build plan followed here). It now switches:
 *   · FALLING LEAVES in the woods (forest density ≥ 0.4, trees actually drawn), within 25 m of the viewer, from the
 *     canopy (5–14 m) down to the ground, fluttering (a leaf turning edge-on) and carried by the wind;
 *   · POLLEN MOTES over open meadow by day (fair, warm, not the desert / the crystals), drifting in a 12 m bubble;
 *   · a DUST DEVIL in the dune desert when the wind blows (≥ 0.5) and no sandstorm owns the air — one spinning column
 *     60–140 m off, wandering downwind, living 20–40 s;
 *   · and the woods' INSECTS (the fauna module's gnat / moth / firefly swarm) — the "ambient critters" the XR profile
 *     names — which already ran under `particleCap` and now also answer the flag.
 * Sizes follow `particleCap` (the tier's particle budget): nothing at 0.
 * Everything here is deterministic given an rng; the module (AtlasAmbientLifeModule) owns the world and the draws.
 */

import { inverseDistanceGain } from './audio/spatial_math.js';

export type AmbientRng = () => number;

export const LEAF = {
  /** Forest density (lib/forest_field) from which leaves fall. */
  forestMin: 0.4,
  /** Leaves fall inside this radius of the viewer (m); a leaf drifting past 1.4× is recycled. */
  radius: 25,
  /** Spawn height over the ground (m) — the canopy band. */
  spawnH: [5, 14] as const,
  /** Fall speed (m/s) — a leaf's terminal velocity (≈ 0.5–1.5 m/s for broad leaves). */
  fall: [0.55, 1.1] as const,
  /** Quad size (m). The silhouette fills about half its quad: at 7–12 cm quads the leaves drawn were 4–6 cm — specks at
   *  arm's length in the bed's picture (2026-09-29). A 14–24 cm quad draws a 7–13 cm leaf. */
  size: [0.14, 0.24] as const,
  /** Lateral sway amplitude (m/s) and its angular rate (rad/s). */
  sway: 0.45,
  swayRate: [1.4, 2.6] as const,
  /** Leaves at particleCap 1 and full density. */
  maxAtCap1: 160,
  /**
   * THE LANDING (particles polish, 2026-09-29): a leaf that reaches the ground used to VANISH the frame it touched (a
   * pop right where the eye follows it). It now flops flat over `settleS`, lies there `restS` and dissolves over
   * `fadeS` (a screen-space dither on its per-instance fade — the alpha-tested silhouette stays crisp).
   */
  settleS: 0.35,
  restS: 1.5,
  fadeS: 3,
  /** A new leaf dissolves IN over this (it used to appear mid-air at full size in view). */
  fadeInS: 0.6,
  /** A flat leaf rides this far over the RENDERED ground, so the quad never z-fights it. */
  lift: 0.03,
  /**
   * THE REST POSE (particles polish, 2026-09-29): a fluttering leaf rocks about the HORIZONTAL — broad side down, tilting
   * up at the ends of each swing — the way a flat plate falls. The tumble used to rock about the VERTICAL, so a leaf was
   * a standing card at mid-swing: seen from below (looking up into the canopy) it was a sliver, and it never lay flat.
   * `leafTumble` gives the rock angles about this rest; `leafPose` composes them (Euler order `LEAF_EULER`).
   */
  rest: -Math.PI / 2,
} as const;

/** The leaf pose's Euler order: yaw (Y), then a side roll (Z), then the rock about the rest (X) — both rocks tilt the leaf off level. */
export const LEAF_EULER = 'YZX' as const;

export const POLLEN = {
  /** Open meadow only: below this forest density. */
  forestMax: 0.3,
  radius: 12,
  height: [0.3, 2.6] as const,
  /** Sprite size (m) — the glow's bright core is a third of it. At 3–5 cm the probe's picture of a meadow holding ~30
   *  motes showed one speck (2026-09-29): a 2 px core past a few metres. Sunlit fluff reads at 6–10 cm. */
  size: [0.06, 0.1] as const,
  /** Daylight only (night factor ≤ this), fair (precipitation ≤ this), warm (≥ this °C). */
  nightMax: 0.35,
  precipMax: 0.05,
  tempMin: 8,
  /** Drift speed band (m/s) and the wind's share. */
  drift: 0.12,
  windShare: 0.35,
  maxAtCap1: 120,
  /**
   * THE FADES (particles polish, 2026-09-29): a mote fades IN (s) after a staggered start (≤ `stagger` s — a whole
   * meadow never lights in one frame), fades OUT when the budget no longer holds it (it used to be popped the frame the
   * budget dropped), and fades toward the bubble's rim from `edge` × radius, so the wrap to the far side is unseen.
   */
  fadeIn: 1.4,
  fadeOut: 1.6,
  stagger: 1.2,
  edge: 0.72,
} as const;

/** Biomes with a meadow for pollen (the desert and the crystal forest have none). */
export const POLLEN_BIOMES: ReadonlySet<string> = new Set(['regolith_plains', 'moss_wetland', 'rocky_highlands', 'fungal_basin']);

export const DEVIL = {
  biome: 'dune_desert',
  /** The weather's wind strength (0..1) from which a devil can form. */
  windMin: 0.5,
  /** Distance band from the viewer (m) where one forms. */
  dist: [60, 140] as const,
  /** Lifetime (s) and the quiet gap between devils (s). */
  life: [20, 40] as const,
  gap: [25, 60] as const,
  /** Column height (m) and radius at the base / the top (m). */
  height: 18,
  rBase: 1.4,
  rTop: 6,
  /** Sand grains drawn in the column (sprites). */
  grains: 64,
  /**
   * THE SKIRT (particles polish, 2026-09-29): a real devil stands in a low, wide cloud of the dust it lifts — a few
   * big, faint puffs round its foot (radius `skirtR`, puff size `skirtSize`) turn the column's base from a thin spiral
   * of blobs into a column rising out of its own dust. `skirt` more sprites in the same batch (one draw).
   */
  skirt: 14,
  skirtR: [1.8, 4.4] as const,
  skirtSize: [3, 5.5] as const,
  /** Spin (rad/s at the base; the top turns slower) and the travel speed per unit wind strength (m/s). */
  spin: 2.4,
  travel: 4,
  /** Fade in / out (s). */
  fadeIn: 3,
  fadeOut: 4,
} as const;

export interface AmbientEnv {
  /** The preset's `ambientLife` (undefined = on: a profile saved before the flag had no key). */
  enabled: boolean | undefined;
  /** The preset's particle budget 0..1. */
  particleCap: number;
  /** Trees are drawn at all (`foliageDensityScale > 0`). */
  foliageDrawn: boolean;
  forestDensity: number;
  biome: string;
  /** 0 day … 1 night (lib/lighting nightFactor). */
  night: number;
  /** The weather's TARGET particle kind ('rain' | 'snow' | 'dust' | …) and density. */
  precipKind: string;
  precip: number;
  temperatureC: number | null;
  /** The weather's wind strength 0..1. */
  windStrength: number;
  indoors: boolean;
  /** In space, flying a ship, or otherwise well above the ground (the fauna's ALOFT rule). */
  away: boolean;
}

export interface AmbientBudget { leaves: number; pollen: number; devil: boolean; why: string }

const smooth = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** How much ambient life this spot and this preset allow right now. `why` names the gate when everything is off. */
export function ambientBudget(e: AmbientEnv): AmbientBudget {
  if (e.enabled === false) return { leaves: 0, pollen: 0, devil: false, why: 'ambientLife off' };
  const cap = Math.max(0, Math.min(1, e.particleCap || 0));
  if (cap <= 0) return { leaves: 0, pollen: 0, devil: false, why: 'particleCap 0' };
  if (e.indoors) return { leaves: 0, pollen: 0, devil: false, why: 'indoors' };
  if (e.away) return { leaves: 0, pollen: 0, devil: false, why: 'away from the ground' };
  const precipFalling = (e.precipKind === 'rain' || e.precipKind === 'snow') ? e.precip : 0;
  let leaves = 0;
  if (e.foliageDrawn && e.forestDensity >= LEAF.forestMin && e.precipKind !== 'snow') {
    leaves = Math.max(8, Math.round(LEAF.maxAtCap1 * cap * (0.35 + 0.65 * smooth(LEAF.forestMin, 1, e.forestDensity))));
  }
  let pollen = 0;
  const temp = e.temperatureC ?? 18;
  if (POLLEN_BIOMES.has(e.biome) && e.forestDensity < POLLEN.forestMax && e.night <= POLLEN.nightMax
    && precipFalling <= POLLEN.precipMax && e.precipKind !== 'dust' && temp >= POLLEN.tempMin) {
    pollen = Math.max(6, Math.round(POLLEN.maxAtCap1 * cap * (1 - smooth(0.15, POLLEN.nightMax, e.night))));
  }
  const devil = e.biome === DEVIL.biome && e.windStrength >= DEVIL.windMin && e.precipKind !== 'dust' && precipFalling <= 0.05 && e.night < 0.5;
  const why = leaves || pollen || devil ? '' : 'nothing here (not woods, meadow by day, or a windy desert)';
  return { leaves, pollen, devil, why };
}

// ─── leaves ────────────────────────────────────────────────────────────────────
export interface Leaf {
  x: number; y: number; z: number; vy: number; phase: number; rate: number; size: number; tint: number; variant: number;
  /** Seconds since it spawned (the fade-in). */
  age: number;
  /** −1 aloft; else seconds since it touched the ground (settle → rest → dissolve). */
  settled: number;
  /** The rendered ground under it when it landed. */
  gy: number;
}

/** A new leaf in the canopy band. `into` recycles a finished leaf (the module keeps a pool: no allocation per spawn). */
export function spawnLeaf(rng: AmbientRng, px: number, pz: number, groundAt: (x: number, z: number) => number, into?: Leaf): Leaf {
  const a = rng() * Math.PI * 2, d = Math.sqrt(rng()) * LEAF.radius;
  const x = px + Math.cos(a) * d, z = pz + Math.sin(a) * d;
  const h = LEAF.spawnH[0] + rng() * (LEAF.spawnH[1] - LEAF.spawnH[0]);
  const l = into ?? ({} as Leaf);
  l.x = x; l.y = groundAt(x, z) + h; l.z = z;
  l.vy = -(LEAF.fall[0] + rng() * (LEAF.fall[1] - LEAF.fall[0]));
  l.phase = rng() * Math.PI * 2; l.rate = LEAF.swayRate[0] + rng() * (LEAF.swayRate[1] - LEAF.swayRate[0]);
  l.size = LEAF.size[0] + rng() * (LEAF.size[1] - LEAF.size[0]);
  l.tint = Math.floor(rng() * LEAF_TINTS.length); l.variant = Math.floor(rng() * 3);
  l.age = 0; l.settled = -1; l.gy = 0;
  return l;
}

/** Earthy leaf colours (sRGB): greens, yellows, browns, a rust red. */
export const LEAF_TINTS = [0x6f8f3a, 0x8aa04a, 0xc9a33a, 0xd8b85a, 0x9a6a36, 0x7e5a30, 0xa64a2a] as const;

/** One step of a falling leaf: its fall, the side-to-side flutter and the wind's drift (m/s × dt). A leaf on the ground only ages. */
export function stepLeaf(l: Leaf, dt: number, windX: number, windZ: number, windStrength: number): void {
  l.age += dt;
  if (l.settled >= 0) { l.settled += dt; return; } // lying on the ground: the tumble's phase is frozen at the landing
  l.phase += l.rate * dt;
  const sway = Math.sin(l.phase) * LEAF.sway;
  const push = 0.6 + 1.8 * Math.max(0, Math.min(1, windStrength));
  // the flutter swings across the wind; the wind carries the leaf along it
  l.x += (windX * push + -windZ * sway) * dt;
  l.z += (windZ * push + windX * sway) * dt;
  // it falls slowest broad side down (level, mid-swing) and slips down fastest when tipped steepest at the swing's ends
  l.y += l.vy * (0.65 + 0.35 * Math.abs(Math.sin(l.phase))) * dt;
}

/**
 * A falling leaf's TUMBLE (ambient life wave, 2026-09-29 — the leaves are lit 3D quads now, not sprites): it rocks with
 * the flutter (level mid-swing, tipped steepest at the ends, where `stepLeaf` makes it slip down fastest), rolls a
 * little out of step, and slowly spins round as it falls. Radians, about `LEAF.rest` (level); `yaw` is unbounded.
 */
export function leafTumble(l: Leaf, out?: { pitch: number; yaw: number; roll: number }): { pitch: number; yaw: number; roll: number } {
  const o = out ?? { pitch: 0, yaw: 0, roll: 0 };
  o.pitch = 1.15 * Math.sin(l.phase); o.yaw = l.phase * 0.35 + l.variant * 2.1; o.roll = 0.5 * Math.cos(l.phase * 0.7);
  return o;
}

/** The drawn size of a fluttering leaf: it turns edge-on and back (never below a third of its face). */
export function leafDrawnSize(l: Leaf): number { return l.size * (0.35 + 0.65 * Math.abs(Math.cos(l.phase * 0.5))); }

/** A leaf is done FALLING when it lies on the ground or drifted out of the viewer's woods (see `leafTouchdown` / `leafOut`). */
export function leafDone(l: Leaf, groundY: number, px: number, pz: number): boolean {
  return l.y <= groundY + 0.02 || leafOut(l, px, pz);
}

/** Drifted out of the viewer's woods (recycled — its edge fade has already taken it to nothing). */
export function leafOut(l: Leaf, px: number, pz: number): boolean { return Math.hypot(l.x - px, l.z - pz) > LEAF.radius * 1.4; }

/** How far a leaf's quad reaches below its centre, tipped `rock` about the level rest and `roll` to the side (order YZX): (s/2)(|sin roll| + |sin rock · cos roll|). */
export function leafHalfDrop(size: number, rock: number, roll: number): number {
  return 0.5 * size * (Math.abs(Math.sin(roll)) + Math.abs(Math.sin(rock) * Math.cos(roll)));
}

/** The lowest corner of a falling leaf has reached the ground (the whole quad, not its centre: a tipped leaf used to sink its low edge into the grass first). */
export function leafTouchdown(l: Leaf, groundY: number): boolean {
  if (l.settled >= 0) return false;
  const p = 1.15 * Math.sin(l.phase), r = 0.5 * Math.cos(l.phase * 0.7);
  return l.y - leafHalfDrop(l.size, p, r) <= groundY + LEAF.lift;
}

/** It lands: the tumble freezes where it touched, and from here it only flops flat, rests and dissolves. */
export function settleLeaf(l: Leaf, groundY: number): void { l.settled = 0; l.gy = groundY; }

/** Rested and dissolved — recycle it. */
export function leafGone(l: Leaf): boolean { return l.settled >= LEAF.restS + LEAF.fadeS; }

export interface LeafPose { x: number; y: number; z: number; pitch: number; yaw: number; roll: number }

/**
 * Where and how to draw a leaf (Euler order `LEAF_EULER`, about the level rest): aloft its tumble; on the ground it
 * flops FLAT from the pose it landed in (both rocks eased to level over `settleS`) with its lowest corner kept on the
 * ground while it tips, then lies at `lift` over the rendered ground. Writes into `out` (no allocation per leaf per frame).
 */
export function leafPose(l: Leaf, out: LeafPose): LeafPose {
  const p = 1.15 * Math.sin(l.phase), r = 0.5 * Math.cos(l.phase * 0.7);
  out.x = l.x; out.z = l.z; out.yaw = l.phase * 0.35 + l.variant * 2.1;
  if (l.settled < 0) { out.pitch = LEAF.rest + p; out.roll = r; out.y = l.y; return out; }
  const k = 1 - smooth(0, LEAF.settleS, l.settled);
  out.pitch = LEAF.rest + p * k; out.roll = r * k;
  out.y = l.gy + LEAF.lift + leafHalfDrop(l.size, p * k, r * k);
  return out;
}

/**
 * A leaf's opacity 0..1 (the module dithers it): it dissolves in after spawning, dissolves toward the edge of the
 * viewer's woods (1.15 → 1.4 × the radius, where it is recycled), and on the ground rests, then dissolves out.
 */
export function leafAlpha(l: Leaf, px: number, pz: number): number {
  const inA = smooth(0, LEAF.fadeInS, l.age);
  const d = Math.hypot(l.x - px, l.z - pz);
  const edge = 1 - smooth(LEAF.radius * 1.15, LEAF.radius * 1.4, d);
  const ground = l.settled < 0 ? 1 : 1 - smooth(LEAF.restS, LEAF.restS + LEAF.fadeS, l.settled);
  return inA * edge * ground;
}

/** New leaves this step so `target` stay aloft: a leaf lives ≈ mean height / mean fall ≈ 11 s. */
export function leafSpawnCount(alive: number, target: number, dt: number, carry: number): { n: number; carry: number } {
  if (alive >= target) return { n: 0, carry: 0 };
  const meanLife = ((LEAF.spawnH[0] + LEAF.spawnH[1]) / 2) / ((LEAF.fall[0] + LEAF.fall[1]) / 2 * 0.82);
  const want = carry + (target / meanLife) * dt + (alive === 0 ? target * 0.25 : 0); // a first burst so woods never start bare
  const n = Math.min(target - alive, Math.floor(want));
  return { n, carry: Math.min(4, want - n) };
}

// ─── pollen ────────────────────────────────────────────────────────────────────
export interface Mote {
  x: number; y: number; z: number; phase: number; size: number; h: number;
  /** Seconds since it spawned — negative while its staggered start is still to come (the fade-in). */
  age: number;
  /** −1 while the budget holds it; else seconds into its fade-out. */
  leaving: number;
}

export function spawnMote(rng: AmbientRng, px: number, pz: number): Mote {
  const a = rng() * Math.PI * 2, d = Math.sqrt(rng()) * POLLEN.radius;
  return {
    x: px + Math.cos(a) * d, y: 0, z: pz + Math.sin(a) * d, phase: rng() * 100,
    h: POLLEN.height[0] + rng() * (POLLEN.height[1] - POLLEN.height[0]), size: POLLEN.size[0] + rng() * (POLLEN.size[1] - POLLEN.size[0]),
    age: -rng() * POLLEN.stagger, leaving: -1,
  };
}

const hash01 = (v: number): number => { const s = Math.sin(v * 12.9898) * 43758.5453; return s - Math.floor(s); };

/**
 * A mote drifts on a slow curl, carried by the wind. THE FIELD IS THE WORLD'S (particles polish, 2026-09-29): a mote
 * keeps its WORLD position — it used to ride at a fixed offset from the viewer, so a walker carried a halo of pollen
 * along (no parallax at all). Past the bubble it re-enters on the far side at the faded rim (unseen); a jump far beyond
 * it (a teleport) re-seeds it inside the bubble and fades it in again.
 */
export function stepMote(m: Mote, dt: number, px: number, pz: number, groundY: number, windX: number, windZ: number, windStrength: number): void {
  m.phase += dt;
  m.age += dt;
  if (m.leaving >= 0) m.leaving += dt;
  const w = POLLEN.windShare * (0.3 + windStrength);
  m.x += (Math.sin(m.phase * 0.7) * POLLEN.drift + windX * w) * dt;
  m.z += (Math.cos(m.phase * 0.53) * POLLEN.drift + windZ * w) * dt;
  const dx = m.x - px, dz = m.z - pz, r = Math.hypot(dx, dz);
  if (r > POLLEN.radius * 2) {
    // a jump far past the bubble (a teleport): a fresh hashed spot in the disc — through the viewer, every mote came
    // from the same old place and landed on ONE line (the world picture, 2026-09-29)
    const a = Math.PI * 2 * hash01(m.phase * 1.37 + 0.5), back = POLLEN.radius * Math.sqrt(hash01(m.phase));
    m.x = px + Math.cos(a) * back; m.z = pz + Math.sin(a) * back;
    m.age = Math.min(m.age, 0);
  } else if (r > POLLEN.radius) {
    m.x = px - (dx / r) * POLLEN.radius * 0.96; m.z = pz - (dz / r) * POLLEN.radius * 0.96;
  }
  m.y = groundY + m.h + Math.sin(m.phase * 1.3) * 0.25;
}

/** A mote twinkles as it turns in the sun: 0.45..1. */
export function moteGlint(m: Mote): number { return 0.45 + 0.55 * Math.max(0, Math.sin(m.phase * 2.1)); }

/** A mote's fade 0..1 (the glint rides on top): in after its staggered start, out when released, and toward the bubble's rim. */
export function moteAlpha(m: Mote, px: number, pz: number): number {
  const inA = smooth(0, POLLEN.fadeIn, m.age);
  const outA = m.leaving < 0 ? 1 : 1 - smooth(0, POLLEN.fadeOut, m.leaving);
  const edge = 1 - smooth(POLLEN.radius * POLLEN.edge, POLLEN.radius, Math.hypot(m.x - px, m.z - pz));
  return inA * outA * edge;
}

/** The budget no longer holds it: it fades out (never popped). */
export function releaseMote(m: Mote): void { if (m.leaving < 0) m.leaving = 0; }

/** Faded out after its release — recycle it. */
export function moteGone(m: Mote): boolean { return m.leaving >= POLLEN.fadeOut; }

// ─── dust devils ───────────────────────────────────────────────────────────────
export interface Devil { x: number; z: number; age: number; life: number; seed: number; hx: number; hz: number }

/** Where a new devil forms: DEVIL.dist from the viewer, upwind so the wind carries it across the view. */
export function spawnDevil(rng: AmbientRng, px: number, pz: number, windX: number, windZ: number): Devil {
  const d = DEVIL.dist[0] + rng() * (DEVIL.dist[1] - DEVIL.dist[0]);
  const up = Math.atan2(-windZ, -windX); // the bearing the wind blows FROM
  const a = up + (rng() - 0.5) * 1.6;
  return { x: px + Math.cos(a) * d, z: pz + Math.sin(a) * d, age: 0, life: DEVIL.life[0] + rng() * (DEVIL.life[1] - DEVIL.life[0]), seed: rng() * 1000, hx: windX, hz: windZ };
}

/** The column wanders downwind with a lazy meander. */
export function stepDevil(v: Devil, dt: number, windX: number, windZ: number, windStrength: number): void {
  v.age += dt;
  const speed = DEVIL.travel * Math.max(0.3, windStrength);
  const m = Math.sin(v.age * 0.4 + v.seed) * 0.5;
  v.hx += (windX - v.hx) * Math.min(1, dt * 0.5); v.hz += (windZ - v.hz) * Math.min(1, dt * 0.5);
  v.x += (v.hx - v.hz * m) * speed * dt;
  v.z += (v.hz + v.hx * m) * speed * dt;
}

/** 0..1: fades in, holds, fades out over its life. */
export function devilAlpha(v: Devil): number {
  const inA = Math.min(1, v.age / DEVIL.fadeIn), outA = Math.min(1, Math.max(0, (v.life - v.age) / DEVIL.fadeOut));
  return Math.max(0, Math.min(inA, outA));
}

export interface DevilSprite { x: number; y: number; z: number; size: number; alpha: number }

/** Grain `i` of `n` in the column at the devil's age: a spiral whose radius widens with height and whose top turns slower. Writes into `out` when given (the module: no allocation per grain per frame). */
export function devilGrain(v: Devil, i: number, n: number, groundY: number, out?: DevilSprite): DevilSprite {
  const f = (i + 0.5) / n;
  const h = Math.pow(f, 0.85) * DEVIL.height;
  const hf = h / DEVIL.height;
  const r = DEVIL.rBase + (DEVIL.rTop - DEVIL.rBase) * hf;
  const spin = DEVIL.spin * (1.25 - 0.6 * hf);
  const ang = (i * 2.399963) + v.seed + v.age * spin; // the golden angle spreads the grains round the column
  const lean = hf * hf * 2.5; // the top leans downwind
  const o = out ?? { x: 0, y: 0, z: 0, size: 0, alpha: 0 };
  // a puff orbits at 0.6 of the column's radius and reaches past it with its own size: opposite puffs overlap into one
  // body — at the full radius the wide top split into two lobes with sky between them (the world picture, 2026-09-29)
  o.x = v.x + Math.cos(ang) * r * 0.6 + v.hx * lean;
  o.y = groundY + 0.2 + h + Math.sin(v.age * 3 + i) * 0.15;
  o.z = v.z + Math.sin(ang) * r * 0.6 + v.hz * lean;
  // a grain is a PUFF as wide as the column where it rides, so its neighbours overlap into one body of dust — at a fixed
  // 0.7–2.3 m the column was a loose spiral of beads (the bed's picture, 2026-09-29); faint each, dense where they stack
  o.size = 0.5 + 1.25 * r;
  o.alpha = devilAlpha(v) * (0.42 - 0.3 * hf); // dense low, thinning to a faint plume (it read top-heavy, a mushroom)
  return o;
}

/**
 * Puff `j` of `n` in the devil's SKIRT — the low, wide dust cloud round its foot: big faint puffs orbiting a little
 * faster than the column's base, breathing in size, their bottoms kept near the ground (a big sprite sunk into the
 * sand would show a hard cut where it meets the terrain).
 */
export function devilSkirt(v: Devil, j: number, n: number, groundY: number, out?: DevilSprite): DevilSprite {
  const f = (j + 0.5) / n;
  const r = DEVIL.skirtR[0] + (DEVIL.skirtR[1] - DEVIL.skirtR[0]) * hash01(v.seed + j * 7.13);
  const ang = j * 2.399963 + v.seed * 1.7 + v.age * DEVIL.spin * 0.8;
  const breathe = 1 + 0.15 * Math.sin(v.age * 1.9 + j * 1.3);
  const size = (DEVIL.skirtSize[0] + (DEVIL.skirtSize[1] - DEVIL.skirtSize[0]) * f) * breathe;
  const o = out ?? { x: 0, y: 0, z: 0, size: 0, alpha: 0 };
  o.x = v.x + Math.cos(ang) * r;
  o.y = groundY + size * 0.42 + 0.8 * hash01(v.seed + j * 3.71);
  o.z = v.z + Math.sin(ang) * r;
  o.size = size;
  o.alpha = devilAlpha(v) * (0.18 + 0.08 * (1 - f));
  return o;
}

/**
 * The devil's wind, heard (particles polish, 2026-09-29): the loop's gain at the listener — its life (`devilAlpha`)
 * × an INVERSE-distance law (full within `refM`, then ref/d, tapered to silence at `rangeM`). The linear
 * `1 − d/range` it replaced kept a far devil nearly as loud as a near one (half the peak at 85 m, where the inverse
 * law gives a fifth) and cut it hard at the edge. The loop's panner is direction-only: this is its ONE distance owner.
 */
export const DEVIL_SOUND = { peak: 0.75, refM: 18, rangeM: 170 } as const;
export function devilHeard(alpha: number, distM: number): number {
  const a = Math.max(0, Math.min(1, alpha));
  return a <= 0 ? 0 : DEVIL_SOUND.peak * a * inverseDistanceGain(distM, DEVIL_SOUND.refM, DEVIL_SOUND.rangeM);
}

/** The quiet gap before the next devil (s). */
export function devilGap(rng: AmbientRng): number { return DEVIL.gap[0] + rng() * (DEVIL.gap[1] - DEVIL.gap[0]); }
